import React, { useState, useEffect } from 'react';
import { Modal } from './modalComponents';
import { getTruckTransactions, getTruckRefills } from './api';
import { fmt, fmtUSD } from './formatters';
import { Fuel, DollarSign, Calendar, Activity } from 'lucide-react';

const TruckTransactionsModal = ({ isOpen, onClose, truck }) => {
  const [transactions, setTransactions] = useState([]);
  const [refills, setRefills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('sales');

  useEffect(() => {
    if (isOpen && truck) {
      const loadData = async () => {
        setLoading(true);
        try {
          const [txData, refillData] = await Promise.all([
            getTruckTransactions(truck.id, 50),
            getTruckRefills(truck.id, 50)
          ]);
          setTransactions(txData);
          setRefills(refillData);
        } catch (err) {
          console.error('Failed to load truck history', err);
        } finally {
          setLoading(false);
        }
      };
      loadData();
    }
  }, [isOpen, truck]);

  if (!truck) return null;

  return (
    <Modal 
      title={`History: ${truck.truck_number}`} 
      onClose={onClose}
    >
      <div className="space-y-4">
        {/* Stats rapides */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-gray-700/30 rounded-lg p-2 text-center">
            <Fuel size={14} className="text-blue-400 mx-auto mb-1" />
            <p className="text-white text-sm font-bold">{fmt(truck.current_volume_gallons)}</p>
            <p className="text-gray-500 text-xs">Current Fuel</p>
          </div>
          <div className="bg-gray-700/30 rounded-lg p-2 text-center">
            <Activity size={14} className="text-green-400 mx-auto mb-1" />
            <p className="text-white text-sm font-bold">{transactions.length}</p>
            <p className="text-gray-500 text-xs">Sales</p>
          </div>
          <div className="bg-gray-700/30 rounded-lg p-2 text-center">
            <Fuel size={14} className="text-yellow-400 mx-auto mb-1" />
            <p className="text-white text-sm font-bold">{refills.length}</p>
            <p className="text-gray-500 text-xs">Refills</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-700">
          <button 
            onClick={() => setActiveTab('sales')}
            className={`px-3 py-1 text-sm transition ${activeTab === 'sales' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400'}`}
          >
            Fuel Sales
          </button>
          <button 
            onClick={() => setActiveTab('refills')}
            className={`px-3 py-1 text-sm transition ${activeTab === 'refills' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400'}`}
          >
            Refill History
          </button>
        </div>

        {/* Contenu */}
        <div className="max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading...</div>
          ) : activeTab === 'sales' ? (
            transactions.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No fuel sales recorded</p>
            ) : (
              <div className="space-y-2">
                {transactions.map(tx => (
                  <div key={tx.id} className="bg-gray-700/30 rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-white text-sm font-medium">
                          {tx.gallons} gal · {fmtUSD(tx.total_amount)}
                        </p>
                        <p className="text-gray-500 text-xs">
                          Aircraft ID: {tx.aircraft_id?.slice(0, 8)}...
                        </p>
                      </div>
                      <p className="text-gray-500 text-xs">
                        {new Date(tx.timestamp).toLocaleString('en-US')}
                      </p>
                    </div>
                    {tx.notes && <p className="text-gray-500 text-xs mt-1">{tx.notes}</p>}
                  </div>
                ))}
              </div>
            )
          ) : (
            refills.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No refills recorded</p>
            ) : (
              <div className="space-y-2">
                {refills.map(refill => (
                  <div key={refill.id} className="bg-gray-700/30 rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-white text-sm font-medium">
                          +{refill.gallons} gal
                        </p>
                        <p className="text-gray-500 text-xs">
                          Source: {refill.source_tank_type === 'station' ? 'Station Tank' : 'Direct Delivery'}
                        </p>
                      </div>
                      <p className="text-gray-500 text-xs">
                        {new Date(refill.timestamp).toLocaleString('en-US')}
                      </p>
                    </div>
                    {refill.notes && <p className="text-gray-500 text-xs mt-1">{refill.notes}</p>}
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </Modal>
  );
};

export default TruckTransactionsModal;